/**
 * Feedback, sent straight to an inbox — the one deliberate exception to
 * "the app makes no API calls" (see DESIGN.md §2). Everything else here
 * works with no signal; this is the one thing that cannot, because handing
 * a message to a person is inherently a network operation. It is opt-in,
 * behind an explicit "Send feedback" action, never on the path to building
 * a code.
 *
 * Lives under lib/net/, not lib/ directly — the rest of lib/ must stay free
 * of fetch (see DESIGN.md §2, "The network boundary"). This file, and the
 * Worker at worker/index.ts, are the only two places in the app allowed to
 * call a network.
 *
 * Posts to this app's own Worker at /api/contact, which holds the Turnstile
 * secret and the Cloudflare Email Service binding — neither of which can live
 * in a static bundle. The honeypot below stops the median bot that fills
 * every field it finds on a rendered page; Turnstile is what stops the rest,
 * verified server-side in worker/index.ts.
 */

export const FEEDBACK_MESSAGE_MAX = 2000;

export interface FeedbackResult {
  ok: boolean;
  message: string;
}

/** `honeypotFilled` is read from an uncontrolled input the diver never sees —
 *  see FeedbackDialog for why it must not be React-controlled state. */
export async function sendFeedback(
  message: string,
  email: string,
  honeypotFilled: boolean,
  turnstileToken: string | null,
): Promise<FeedbackResult> {
  if (honeypotFilled) {
    // Tell the bot it worked. The only wrong move here is a response that
    // teaches it which field gave it away.
    return { ok: true, message: "Sent." };
  }

  if (!turnstileToken) {
    return { ok: false, message: "Verification isn't ready yet — wait a moment and try again." };
  }

  try {
    const response = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: message.slice(0, FEEDBACK_MESSAGE_MAX),
        ...(email.trim() ? { email: email.trim() } : {}),
        token: turnstileToken,
      }),
    });

    if (response.ok) return { ok: true, message: "Sent — thanks." };

    const body: unknown = await response.json().catch(() => null);
    const code =
      body && typeof body === "object" ? (body as { error?: unknown }).error : undefined;

    return { ok: false, message: messageForError(typeof code === "string" ? code : undefined) };
  } catch {
    // No network, or the request never landed — the one path in this app
    // that genuinely needs a connection, so say so rather than fail quietly.
    return {
      ok: false,
      message: "Couldn't send. Unlike everything else here, this needs a connection — try again once you have one.",
    };
  }
}

function messageForError(code: string | undefined): string {
  switch (code) {
    case "challenge_failed":
      return "Verification failed — try again.";
    case "too_long":
      return "That message is too long.";
    default:
      return "That was not accepted. Try again in a moment.";
  }
}
